import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { analyticsAPI, exportAPI, filesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  FolderKanban,
  FileText,
  DollarSign,
  TrendingUp,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ArrowRight,
  MapPin,
  Download,
  Plus,
  Upload,
  File,
  X,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import IndicatorSelector from './IndicatorSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const VERIFICATION_TYPES = [
  'Full Project Verification',
  'Financial Audit',
  'Impact Assessment',
  'Beneficiary Verification',
  'Progress Monitoring',
  'Compliance Check',
  'Other'
];

export default function DonorDashboard() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [projectForm, setProjectForm] = useState({
    title: '',
    organization_name: user?.organization_name || '',
    country: '',
    region: '',
    city: '',
    street_address: '',
    postal_code: '',
    description: '',
    start_date: '',
    end_date: '',
    budget: '',
    budget_breakdown: '',
    goals: '',
    indicators: [],
    verification_type: ''
  });

  // newIndicator state removed — handled by IndicatorSelector component

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (user?.organization_name) {
      setProjectForm(prev => ({ ...prev, organization_name: user.organization_name }));
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [analyticsRes, chartRes, projectsRes, reportsRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`, { headers: getAuthHeader() }),
        axios.get(`${API}/analytics/chart-data`, { headers: getAuthHeader() }),
        axios.get(`${API}/projects`, { headers: getAuthHeader() }),
        axios.get(`${API}/reports`, { headers: getAuthHeader() })
      ]);
      setAnalytics(analyticsRes.data);
      setChartData(chartRes.data);
      setProjects(projectsRes.data);
      setRecentReports(reportsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    for (const file of files) {
      if (file.size > MAX_SIZE) {
        toast.error(`${file.name} exceeds 10MB limit`);
        return;
      }
    }

    setUploading(true);
    
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/files/upload`, formData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
        });
        
        setUploadedFiles(prev => [...prev, {
          file_id: response.data.file_id,
          filename: response.data.filename
        }]);
      }
      toast.success('Files uploaded successfully');
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.file_id !== fileId));
  };

  const handleUpdateIndicators = (indicators) => {
    setProjectForm(prev => ({ ...prev, indicators }));
  };

  const handleSubmitProject = async (e) => {
    e.preventDefault();
    
    if (!projectForm.title || !projectForm.description || !projectForm.country) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        ...projectForm,
        budget: projectForm.budget ? parseFloat(projectForm.budget) : null,
        document_ids: uploadedFiles.map(f => f.file_id)
      };

      await axios.post(`${API}/projects`, payload, { headers: getAuthHeader() });
      
      toast.success('Project submitted for verification! Admin will review shortly.');
      setShowSubmitDialog(false);
      setProjectForm({
        title: '',
        organization_name: user?.organization_name || '',
        country: '',
        region: '',
        description: '',
        start_date: '',
        end_date: '',
        budget: '',
        budget_breakdown: '',
        goals: '',
        indicators: [],
        verification_type: ''
      });
      setUploadedFiles([]);
      fetchData();
    } catch (error) {
      console.error('Error submitting project:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit project');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportReport = async (projectId) => {
    try {
      const response = await axios.get(`${API}/export/project/${projectId}`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `project_${projectId}_report.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending_review': 'Pending Review',
      'approved': 'Approved',
      'agent_assigned': 'Agent Assigned',
      'in_progress': 'In Progress',
      'awaiting_agents': 'Awaiting Agent Reports',
      'one_report_submitted': 'One Report Submitted',
      'both_reports_submitted': 'Both Reports Submitted',
      'report_submitted': 'Report Submitted',
      'under_admin_review': 'Under Admin Review',
      'verification_complete': 'Verification Complete',
      'released_to_donor': 'Released to Donor',
      'donor_feedback': 'Donor Feedback',
      'revision_required': 'Revision Required',
      'completed': 'Completed',
      'rejected': 'Rejected'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending_review': 'bg-amber-100 text-amber-700',
      'approved': 'bg-blue-100 text-blue-700',
      'agent_assigned': 'bg-purple-100 text-purple-700',
      'in_progress': 'bg-cyan-100 text-cyan-700',
      'awaiting_agents': 'bg-purple-100 text-purple-700',
      'one_report_submitted': 'bg-violet-100 text-violet-700',
      'both_reports_submitted': 'bg-indigo-100 text-indigo-700',
      'report_submitted': 'bg-purple-100 text-purple-700',
      'under_admin_review': 'bg-orange-100 text-orange-700',
      'verification_complete': 'bg-indigo-100 text-indigo-700',
      'released_to_donor': 'bg-teal-100 text-teal-700',
      'donor_feedback': 'bg-emerald-100 text-emerald-700',
      'revision_required': 'bg-rose-100 text-rose-700',
      'completed': 'bg-emerald-100 text-emerald-700',
      'rejected': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const CHART_COLORS = ['#0F172A', '#10B981', '#64748B', '#F59E0B', '#EF4444'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 page-enter" data-testid="donor-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Manrope']">
            Welcome, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-slate-500 mt-1">
            {user?.organization_name && <span className="font-medium">{user.organization_name}</span>}
            {user?.organization_name && ' · '}
            Submit projects and track verification progress
          </p>
        </div>
        <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
          <DialogTrigger asChild>
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-project-btn">
              <Plus className="w-5 h-5 mr-2" />
              Submit New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Submit Project for Verification</DialogTitle>
              <DialogDescription>
                Provide project details for M&E verification
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmitProject} className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="title">Project Title *</Label>
                  <Input
                    id="title"
                    value={projectForm.title}
                    onChange={(e) => setProjectForm({ ...projectForm, title: e.target.value })}
                    placeholder="Water Well Construction Project"
                    required
                    data-testid="project-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="organization_name">Organization Name *</Label>
                  <Input
                    id="organization_name"
                    value={projectForm.organization_name}
                    onChange={(e) => setProjectForm({ ...projectForm, organization_name: e.target.value })}
                    placeholder="Your organization"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="verification_type">Type of Verification</Label>
                  <Select
                    value={projectForm.verification_type}
                    onValueChange={(v) => setProjectForm({ ...projectForm, verification_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {VERIFICATION_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="country">Country *</Label>
                  <Input
                    id="country"
                    value={projectForm.country}
                    onChange={(e) => setProjectForm({ ...projectForm, country: e.target.value })}
                    placeholder="Kenya"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="region">Region / Province</Label>
                  <Input
                    id="region"
                    value={projectForm.region}
                    onChange={(e) => setProjectForm({ ...projectForm, region: e.target.value })}
                    placeholder="Nairobi Province"
                  />
                </div>
                <div>
                  <Label htmlFor="city">City / Town</Label>
                  <Input
                    id="city"
                    value={projectForm.city}
                    onChange={(e) => setProjectForm({ ...projectForm, city: e.target.value })}
                    placeholder="Nairobi"
                    data-testid="project-city-input"
                  />
                </div>
                <div>
                  <Label htmlFor="street_address">Street Address</Label>
                  <Input
                    id="street_address"
                    value={projectForm.street_address}
                    onChange={(e) => setProjectForm({ ...projectForm, street_address: e.target.value })}
                    placeholder="123 Kenyatta Avenue, Building 4"
                    data-testid="project-street-input"
                  />
                </div>
                <div>
                  <Label htmlFor="postal_code">Postal / ZIP Code</Label>
                  <Input
                    id="postal_code"
                    value={projectForm.postal_code}
                    onChange={(e) => setProjectForm({ ...projectForm, postal_code: e.target.value })}
                    placeholder="00100"
                    data-testid="project-postal-input"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Project Description *</Label>
                <Textarea
                  id="description"
                  value={projectForm.description}
                  onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                  placeholder="Describe the project objectives, activities, and expected outcomes..."
                  rows={4}
                  required
                  data-testid="project-description-input"
                />
              </div>

              {/* Timeline & Budget */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={projectForm.start_date}
                    onChange={(e) => setProjectForm({ ...projectForm, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={projectForm.end_date}
                    onChange={(e) => setProjectForm({ ...projectForm, end_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="budget">Total Budget ($)</Label>
                  <Input
                    id="budget"
                    type="number"
                    step="0.01"
                    value={projectForm.budget}
                    onChange={(e) => setProjectForm({ ...projectForm, budget: e.target.value })}
                    placeholder="50000"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="budget_breakdown">Budget Breakdown (optional)</Label>
                <Textarea
                  id="budget_breakdown"
                  value={projectForm.budget_breakdown}
                  onChange={(e) => setProjectForm({ ...projectForm, budget_breakdown: e.target.value })}
                  placeholder="Personnel: $15,000&#10;Equipment: $20,000&#10;Operations: $10,000&#10;..."
                  rows={3}
                />
              </div>

              {/* Goals */}
              <div>
                <Label htmlFor="goals">Project Goals</Label>
                <Textarea
                  id="goals"
                  value={projectForm.goals}
                  onChange={(e) => setProjectForm({ ...projectForm, goals: e.target.value })}
                  placeholder="List the main goals and objectives..."
                  rows={3}
                />
              </div>

              {/* Indicators/KPIs */}
              <div>
                <Label>Indicators / KPIs</Label>
                <p className="text-xs text-slate-500 mt-1 mb-3">Select from suggested indicators or add your own. Target values will be verified by field agents.</p>
                <IndicatorSelector
                  selectedIndicators={projectForm.indicators}
                  onIndicatorsChange={handleUpdateIndicators}
                />
              </div>

              {/* Document Upload */}
              <div>
                <Label>Supporting Documents (optional)</Label>
                <div className="mt-2 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
                  <input
                    type="file"
                    id="doc-upload"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                  <label htmlFor="doc-upload" className="cursor-pointer">
                    {uploading ? (
                      <Loader2 className="w-8 h-8 text-slate-400 animate-spin mx-auto" />
                    ) : (
                      <Upload className="w-8 h-8 text-slate-400 mx-auto" />
                    )}
                    <p className="text-sm text-slate-600 mt-2">
                      {uploading ? 'Uploading...' : 'Click to upload documents'}
                    </p>
                    <p className="text-xs text-slate-500">PDF, Word, Excel, Images</p>
                  </label>
                </div>
                {uploadedFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {uploadedFiles.map((file) => (
                      <div key={file.file_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <File className="w-4 h-4 text-slate-500" />
                          <span className="text-sm">{file.filename}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveFile(file.file_id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowSubmitDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="submit-project-form-btn">
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>Submit Project</>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Key Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="stat-card bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-300 text-sm">My Projects</p>
                <p className="text-3xl font-bold mt-1 font-['Manrope']">{analytics?.total_projects || 0}</p>
              </div>
              <FolderKanban className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-500 text-sm">Total Budget</p>
                <p className="text-3xl font-bold text-slate-900 mt-1 font-['Manrope']">
                  ${(analytics?.total_budget || 0).toLocaleString()}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-500 text-sm">Completed</p>
                <p className="text-3xl font-bold text-emerald-600 mt-1 font-['Manrope']">
                  {analytics?.completed_projects || 0}
                </p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-500 text-sm">Verified Reports</p>
                <p className="text-3xl font-bold text-blue-600 mt-1 font-['Manrope']">
                  {analytics?.released_reports || 0}
                </p>
              </div>
              <FileText className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Projects List */}
      <Card className="stat-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold font-['Manrope']">Your Projects</CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/donor/projects">
              View All
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {projects.length > 0 ? (
            <div className="space-y-4">
              {projects.slice(0, 5).map((project) => (
                <div
                  key={project.project_id}
                  className="flex flex-col lg:flex-row lg:items-center justify-between p-4 bg-slate-50 rounded-lg"
                  data-testid={`project-${project.project_id}`}
                >
                  <div className="flex-1 mb-3 lg:mb-0">
                    <h4 className="font-medium text-slate-900">{project.title}</h4>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-slate-500">
                      {project.country && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {[project.city, project.country].filter(Boolean).join(', ')}
                        </span>
                      )}
                      {project.budget && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          ${project.budget.toLocaleString()}
                        </span>
                      )}
                    </div>
                    {/* Progress bar */}
                    <div className="mt-3 max-w-xs">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-slate-500">Progress</span>
                        <span className="font-medium">{project.progress}%</span>
                      </div>
                      <div className="progress-bar">
                        <div className="progress-bar-fill" style={{ width: `${project.progress}%` }}></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                    <span className={`status-badge ${getStatusColor(project.status)}`}>
                      {getStatusLabel(project.status)}
                    </span>
                    {project.verification_mode === 'double' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full">
                        Double Verified
                      </span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      asChild
                    >
                      <Link to={`/donor/projects/${project.project_id}`}>
                        View Details
                      </Link>
                    </Button>
                    {project.status === 'completed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExportReport(project.project_id)}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Report
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FolderKanban className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No projects yet</h3>
              <p className="text-slate-500 mb-4">Submit your first project for M&E verification</p>
              <Button onClick={() => setShowSubmitDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Submit Project
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Charts Row */}
      {projects.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="stat-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-['Manrope']">Project Status</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData?.project_status?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={chartData.project_status}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      dataKey="count"
                      nameKey="status"
                      label={({ status, count }) => `${getStatusLabel(status)}: ${count}`}
                    >
                      {chartData.project_status.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[250px] text-slate-400">
                  No status data available
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-['Manrope']">Budget Overview</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData?.budget_data?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={chartData.budget_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                    <Bar dataKey="budget" name="Budget" fill="#0F172A" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="spent" name="Spent" fill="#10B981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[250px] text-slate-400">
                  No budget data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Verified Reports */}
      {recentReports.length > 0 && (
        <Card className="stat-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold font-['Manrope']">Verified Reports</CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/donor/reports">
                View All
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentReports.map((report) => {
                const statusMap = {
                  released: { label: 'Needs Review', color: 'bg-blue-100 text-blue-700' },
                  donor_approved: { label: 'Accepted', color: 'bg-emerald-100 text-emerald-700' },
                  donor_revision_requested: { label: 'Revision Requested', color: 'bg-amber-100 text-amber-700' },
                };
                const st = statusMap[report.status] || { label: 'Released', color: 'bg-slate-100 text-slate-700' };
                return (
                  <Link
                    to={`/donor/reports/${report.report_id}`}
                    key={report.report_id}
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:border-slate-300 hover:bg-slate-50 transition-colors cursor-pointer"
                    data-testid={`report-link-${report.report_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-slate-500" />
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{report.title}</p>
                        <p className="text-xs text-slate-500">
                          {report.project_title} &bull; {new Date(report.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`status-badge ${st.color}`}>{st.label}</span>
                      <ArrowRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
